import React, { useEffect, useRef, useState } from 'react';
import { useGlobal, globalizationPath, Header, NavLink } from '@rcg/ui-react';
import { Box, Typography } from '@material-ui/core';
import { formatPrice } from '@rcg/pricing-format';
import { Link } from '@rcg/ui-react/lib/components';
import InfoIcon from '@material-ui/icons/Info';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import { navigate } from '@gatsbyjs/reach-router';
import { initScriptTag } from '@rcg/unified-payment-component';
import { Itinerary } from '../../components';
import PaymentStyles from '../Payment/index.style';
import PaymentFooterWithModal from '../../components/PaymentFooterWithModal';
import {
  generateRelativeUrl,
  getSessionStorageItem,
  sha256,
  getCookies,
  safeEncode,
} from '../../commons/utils';
import setCurrentPriceVar from '../../graphql/operations/localState/setPriceCurrentBooking';
import setCurrentBookingVar from '../../graphql/operations/localState/setCurrentBooking';
import {
  SESSION_STORAGE_CURRENT_SAILING,
  SESSION_STORAGE_CURRENT_BOOKING,
  ROOT_PATH,
  SESSION_STORAGE_PRICE_BOOKING,
  NAVIGATION_PATHS,
  BOOKING_STATUS_OFFERED,
  RESPONSE_ERROR,
  UNIFIED_PAYMENT_SERVICE_BRAND,
  UNIFIED_PAYMENT_SERVICE_CHANNEL,
  UNIFIED_PAYMENT_SERVICE_TYPE,
  UNIFIED_PAYMENT_SERVICE_CLIENT,
  UNIFIED_PAYMENT_SERVICE_AUTOMATION_CLIENT,
  SESSION_STORAGE_AUTOMATION_CHANNEL,
  UPC_PAYMENT_OPTIONS,
  COOKIE_KEYS,
  URL_SOURCE_APP,
  SESSION_STORAGE_INITIATED_FROM_EMAIL,
  LOG_APPLICATION_NAME_DIVLINK,
  BOOKING_TYPES,
  REDIRECT_API_URL,
} from '../../commons/constants';
import { CURRENT_BOOKING, CURRENT_BOOKING_GRP, GET_PAYMENT_HISTORY } from '../../graphql/queries';
import { trackAffirmInteraction, trackPaymentCTA } from '../../commons/tracking/tracking';
import Loader from '../../components/Loader';
import theme from '../../theme/booking-theme';
import { LDUser } from '../../commons/ld-user';
import { LOG_DATA } from '../../graphql/mutations';
import performMutationWithLog from '../../commons/loggerUtils';
import Dictionary from '../../commons/dictionary';
import useDictionary from '../../hooks/useDictionary';

const UnifiedPayment = () => {
  const classes = PaymentStyles();
  const {
    renderContent,
    data: { lang, market, localizedDayjs },
  } = useGlobal();
  const getDictionaryValue = useDictionary(Dictionary);
  const [tooltipContent, setTooltipContent] = useState();
  const [tooltipLabel, setTooltipLabel] = useState();
  const [disableBtn, setDisableBtn] = useState(true);
  const [tooltipInfo, setTooltipInfo] = useState({});
  const [paymentResult, setPaymentResult] = useState({});
  const [isScheduledCalled, setIsScheduledCalled] = useState(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [ctaTitle, setCtaTitle] = useState(
    renderContent('amendPaymentFooterSummaryUPButtonLabel', 'Process Payment'),
  );

  const urlPath = globalizationPath(market, lang, ROOT_PATH);
  const [ldFlagsParams, setLdFlagsParams] = useState({
    country: '',
    language: '',
  });
  const [showLoader, setShowLoader] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const [isCheckedCruiseTicket, setIsCheckedCruiseTicket] = useState(true);
  const [showLineError, setShowLineError] = useState(false);
  const [firstValueCheckbox, setFirstValueCheckbox] = useState(true);

  const sailingData = getSessionStorageItem(SESSION_STORAGE_CURRENT_SAILING);
  const currentBookingInfo = getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING);
  const ldFlagsCountry = currentBookingInfo.country;
  const bankOfAmericaFormRef = useRef(null);
  const {
    id: bookingId,
    price: { currencyCode, balanceDueDate, inFinal },
    country,
    sailing,
  } = currentBookingInfo;

  const countryInfo = country || {
    code: 'USA',
    office: { code: 'MIA' },
  };
  const isGrpBooking = currentBookingInfo?.type === BOOKING_TYPES.GRP;
  const isTABooking = currentBookingInfo?.type === BOOKING_TYPES.TA;
  const currentBookingQuery = isGrpBooking || isTABooking ? CURRENT_BOOKING_GRP : CURRENT_BOOKING;

  const [
    excecutePaymentHistory,
    { data: paymentHistoryData, error: paymentHistoryError, loading: paymentHistoryLoading },
  ] = useLazyQuery(GET_PAYMENT_HISTORY, {
    fetchPolicy: 'no-cache',
    skip: isGrpBooking || isTABooking,
  });

  const [executeCurrentBookingQuery, { data: currentBookingData, loading: currentBookingLoading }] =
    useLazyQuery(currentBookingQuery, {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });
  const apolloClient = useApolloClient();

  const handleLogData = (applicationName, payments) => {
    const bookingType = currentBookingData?.currentBooking?.type;
    payments?.forEach((payment) => {
      const { bookingId: bookId, amount, cardType, option } = payment;
      const variables = {
        input: {
          bookingId: bookId?.[0],
          info: {
            applicationName,
            amount,
            cardType,
            paymentType: option,
            bookingType,
          },
        },
      };
      performMutationWithLog(apolloClient, variables, LOG_DATA);
    });
  };

  const handlePayment = () => {
    let triggerUpdateStatusError = false;
    const { status, payments, bookingStatus } = paymentResult;
    if (
      currentBookingInfo.status === BOOKING_STATUS_OFFERED &&
      bookingStatus === BOOKING_STATUS_OFFERED
    ) {
      triggerUpdateStatusError = true;
      const currentBookingInfoClone = { ...currentBookingInfo };
      currentBookingInfoClone.status = bookingStatus;
      setCurrentBookingVar(currentBookingInfoClone);
    }
    const profile = getSessionStorageItem(URL_SOURCE_APP, false);
    const divlink = getSessionStorageItem(SESSION_STORAGE_INITIATED_FROM_EMAIL, false);
    let applicationName = '';
    if (profile) {
      applicationName = profile;
    } else if (divlink) {
      applicationName = LOG_APPLICATION_NAME_DIVLINK;
    }
    if (isScheduledCalled) {
      navigate(`${urlPath}/${NAVIGATION_PATHS[0].dashboard}`, {
        state: {
          upc: {
            status: 'CREATE_SCHEDULE_PAYMENT',
          },
        },
      });

      return;
    }

    if (status === 'PARTIAL') {
      if (applicationName) {
        handleLogData(applicationName, payments);
      }
      localStorage.removeItem(COOKIE_KEYS.CH_BOOKING_INFO);
      executeCurrentBookingQuery();
    }

    if (status === 'SUCCESS') {
      if (applicationName) {
        handleLogData(applicationName, payments);
      }
      localStorage.removeItem(COOKIE_KEYS.CH_BOOKING_INFO);
      navigate(`${urlPath}/${NAVIGATION_PATHS[0].dashboard}`, {
        state: {
          upc: {
            status,
            cardsAccepted: paymentResult.payments,
            triggerUpdateStatusError,
          },
        },
      });
    }
    if (status === 'CUSTOM_SCHEDULED') {
      navigate(`${urlPath}/${NAVIGATION_PATHS[0].dashboard}`, {
        state: {
          upc: {
            status,
            cardsAccepted: paymentResult.payments,
            triggerUpdateStatusError,
          },
        },
      });
    }
  };

  const priceData = getSessionStorageItem(SESSION_STORAGE_PRICE_BOOKING);
  const payRequired = priceData.balanceDue > 0;
  const getAmountWithFormatPrice = (amount) => {
    return formatPrice(amount, currencyCode, true, true, true);
  };
  const amountPerGuest = priceData.depositAmountOwed / currentBookingInfo.guests.length;
  const getPaymentTypes = ({ priceVar }) => {
    const paymentTypeRemainingBalance = {
      description: renderContent(
        'bookingAmendPaymentTypeRemainingBalance',
        'Pay remaining balance Due',
      ),
      boldDescription: localizedDayjs(priceVar.balanceDueDate).format('MMM D, YYYY'),
      amount: priceVar.balanceDue,
      title: getAmountWithFormatPrice(priceVar.balanceDue),
      type: 'remaining_balance',
    };
    const paymentTypeCustomAmount = {
      description: renderContent(
        'bookingAmendPaymentTypeCustomAmountDescription',
        'Pay custom amount',
      ),
      boldDescription: '',
      amount: 0,
      title: renderContent('bookingAmendPaymentTypeCustomAmountTitle', 'Custom amount'),
      type: 'custom',
    };
    const paymentTypeMinimunDeposit = {
      description: renderContent(
        'bookingAmendPaymentTypeMinimunDeposit',
        'Pay the minimum deposit',
      ),
      boldDescription: `${getAmountWithFormatPrice(amountPerGuest)} ${renderContent(
        'bookingAmendPaymentTypeMinimunDeposit',
        'per guest.',
      )}`,
      amount: priceVar.depositAmountOwed,
      title: getAmountWithFormatPrice(priceVar.depositAmountOwed),
      type: 'minimum_deposit',
    };
    const paymentTypes = [paymentTypeRemainingBalance];
    if (priceVar.balanceDue > 0 && !inFinal) {
      paymentTypes.push(paymentTypeCustomAmount);
    }
    if (priceVar.depositAmountOwed && !inFinal) {
      paymentTypes.unshift(paymentTypeMinimunDeposit);
    }
    return paymentTypes;
  };

  const createTooltipLabel = ({ amount }) => {
    return (
      <Box className={classes.tooltipLabel}>
        <Link component="button" underline="none">
          {(tooltipInfo && tooltipInfo.totalAmountFormatted) ||
            formatPrice(amount, currencyCode, true, true, true)}
        </Link>
        <InfoIcon className={classes.infoIcon} color="primary" />
      </Box>
    );
  };

  const paymentTypes = getPaymentTypes({ priceVar: priceData });

  const automationChannel = sessionStorage.getItem(SESSION_STORAGE_AUTOMATION_CHANNEL);

  const mapingLabelToolTipInfo = (paymentOption) => {
    switch (paymentOption) {
      case UPC_PAYMENT_OPTIONS.CREDIT_CARD:
        return renderContent(
          'bookingAmendPaymentFooterUPAmountCreditCard',
          'Amount on credit card',
        );
      case UPC_PAYMENT_OPTIONS.GIFT_CARD:
        return renderContent('bookingAmendPaymentFooterUPAmountGiftCard', 'Amount on gift card ');
      case UPC_PAYMENT_OPTIONS.AFFIRM:
        return renderContent('bookingAmendPaymentFooterUPAffirm', 'Paying with Affirm ');
      case UPC_PAYMENT_OPTIONS.GOOGLE_PAY:
        return renderContent('bookingAmendPaymentFooterUPGooglePay', 'Paying with Google Pay ');
      case UPC_PAYMENT_OPTIONS.APPLE_PAY:
        return renderContent('bookingAmendPaymentFooterUPApplePay', 'Paying with Apple Pay');
      case UPC_PAYMENT_OPTIONS.PAYPAL:
        return renderContent('bookingAmendPaymentFooterUPPaypal', 'Paying with Paypal');
      default:
        return '';
    }
  };

  const submitPayment = () => {
    setIsSubmittingPayment(true);
    if (disableBtn || firstValueCheckbox || !isCheckedCruiseTicket) {
      if (!isCheckedCruiseTicket || firstValueCheckbox) {
        setFirstValueCheckbox(false);
        setShowLineError(true);
        setIsCheckedCruiseTicket(false);
      }
    } else {
      const {
        trigger: { makePayment },
      } = window.UnifiedPaymentComponent;
      makePayment();
    }
  };

  const checkPaymentHistory = () => {
    excecutePaymentHistory();
  };
  const checkPaymentDetail = (paymentDetail) => {
    setTooltipInfo(paymentDetail);
    const isAffirm = paymentDetail.staterooms.some((room) =>
      room.payments.some(({ paymentOption }) => paymentOption === UPC_PAYMENT_OPTIONS.AFFIRM),
    );
    setTooltipLabel(createTooltipLabel({ amount: paymentDetail.totalAmount }));
    if (isAffirm) {
      setCtaTitle(renderContent('amendPaymentFooterSummaryButtonAffirmLabel', 'Pay with Affirm'));
      return;
    }
    if (paymentDetail.isScheduledPayment) {
      setTooltipLabel(createTooltipLabel({ amount: priceData.depositAmountOwed }));
      setCtaTitle(
        renderContent(
          'amendPaymentFooterSummaryUPScheduledButtonLabel',
          'Pay with Schedule Payment',
        ),
      );
      return;
    }

    setCtaTitle(renderContent('amendPaymentFooterSummaryUPButtonLabel', 'Process Payment'));
  };
  const checkIsDisableCTA = (disableCTA) => {
    setDisableBtn(disableCTA);
  };
  const checkPaymentResponse = (paymentResponse) => {
    setPaymentResult(paymentResponse);
  };
  const checkLoading = (isLoading) => {
    setShowLoader(isLoading);
  };
  const checkUTag = (uTag) => {
    const { paymentOption, eventType, payload, type, cardType, makePaymentType } = uTag;
    if (uTag && paymentOption === UPC_PAYMENT_OPTIONS.AFFIRM && eventType === 'INTERACTIONS') {
      trackAffirmInteraction(payload);
    }
    if (uTag && paymentOption === UPC_PAYMENT_OPTIONS.CREDIT_CARD && eventType === 'INTERACTIONS') {
      if (type === 'SUCCESS') {
        trackPaymentCTA(payload, null, type, currentBookingInfo, cardType, makePaymentType);
      }
      if (type === 'ERROR') {
        trackPaymentCTA(null, null, RESPONSE_ERROR, currentBookingInfo, cardType, makePaymentType);
      }
    }
  };
  const applyBankOfAmerica = () => {
    bankOfAmericaFormRef.current.submit();
  };
  const checkScheduleResponse = (status) => {
    if (Object.keys(status).length > 0) {
      if (currentBookingInfo.status === BOOKING_STATUS_OFFERED) {
        setIsScheduledCalled(true);
        return;
      }
      navigate(`${urlPath}/${NAVIGATION_PATHS[0].dashboard}`, {
        state: {
          upc: {
            status,
          },
        },
      });
    }
  };

  // setup bank of america
  let payload = '';
  const roomGuests = currentBookingInfo.guests;
  if (roomGuests.length) {
    payload = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><instantCreditContainer><applicants><applicant><firstName>${roomGuests[0].firstName}</firstName><lastName>${roomGuests[0].lastName}</lastName><applicantAddress><addressLines/><state>${roomGuests[0].stateCode}</state></applicantAddress></applicant></applicants></instantCreditContainer>`;
  }
  const bankOfAmericaSetup = {
    url: process.env.REACT_APP_BANK_OF_AMERICA_URL,
    requestpayload: payload,
    requesthashvalue: sha256(payload, 'hex'),
  };

  const UPCSetup = () => {
    const {
      setup,
      listeners: {
        getPaymentHistory,
        paymentDetail,
        disableCTA,
        getPaymentResponse,
        isLoading,
        uTag,
        getScheduleResponse,
        bankOfAmerica,
      },
    } = window.UnifiedPaymentComponent;
    const trimBookingId = String(parseInt(bookingId, 10));

    const pageView = {
      pageName: 'myres:payment',
      siteSection: 'myres',
      bookingStatus: currentBookingInfo.status,
      bookingId: trimBookingId,
      sailDate: sailing.sailDate,
      shipCode: sailing.itinerary.ship.code,
    };
    const analytics = {
      uTag: {
        pageView,
      },
    };
    const setupProps = {
      bookingId: trimBookingId,
      bookingInfo: {
        orderId: LDUser(),
        countryCode: countryInfo.code,
        currencyCode,
        officeCode: countryInfo.office.code,
        balanceDueDate,
        depositAmount: priceData.depositAmountOwed,
        isDepositPaid: priceData.depositPaid,
        historyTotalAmount: priceData.paymentsApplied,
        packageCode: sailing.itinerary.code,
        shipCode: sailing.itinerary.ship.code,
        sailDate: sailing.sailDate,
        status: currentBookingInfo.status,
        agencyType: currentBookingInfo.type,
        lastName: roomGuests[0].lastName,
      },
      service: {
        env: process.env.REACT_APP_UNIFIED_PAYMENT_ENV,
        brand: UNIFIED_PAYMENT_SERVICE_BRAND,
        channel:
          automationChannel && automationChannel === UNIFIED_PAYMENT_SERVICE_AUTOMATION_CLIENT
            ? automationChannel
            : UNIFIED_PAYMENT_SERVICE_CHANNEL,
        type: UNIFIED_PAYMENT_SERVICE_TYPE,
        client: UNIFIED_PAYMENT_SERVICE_CLIENT,
        language: lang,
      },
      fonts: {
        regularUrl: generateRelativeUrl(process.env.REACT_APP_UNIFIED_PAYMENT_FONT_REGULAR_URL),
        boldUrl: generateRelativeUrl(process.env.REACT_APP_UNIFIED_PAYMENT_FONT_BOLD_URL),
      },
      theme: 'primary',
      custom: {
        backGround: theme.palette.background.default,
      },
      paymentTypes,
      useUPCLoader: false,
      analytics,
    };
    setup(setupProps);
    getPaymentHistory(checkPaymentHistory);
    paymentDetail(checkPaymentDetail);
    disableCTA(checkIsDisableCTA);
    getPaymentResponse(checkPaymentResponse);
    isLoading(checkLoading);
    uTag(checkUTag);
    getScheduleResponse(checkScheduleResponse);
    bankOfAmerica(applyBankOfAmerica);
  };

  useEffect(() => {
    if (paymentHistoryData && !paymentHistoryLoading) {
      const {
        update: { paymentHistory: updatePaymentHistory },
      } = window.UnifiedPaymentComponent;
      let paymentHistory = [];
      if (paymentHistoryData?.currentBooking?.paymentHistory) {
        paymentHistory = paymentHistoryData?.currentBooking?.paymentHistory.map(
          ({ type, amount, postedDate, status, date }) => {
            return {
              paymentDate: postedDate || null,
              paymentType: type,
              amount,
              status,
              date,
            };
          },
        );
      }
      updatePaymentHistory(paymentHistory);
    }
  }, [paymentHistoryData]);

  useEffect(() => {
    if (paymentHistoryError && !paymentHistoryLoading) {
      const {
        update: { paymentHistory: updatePaymentHistory },
      } = window.UnifiedPaymentComponent;
      const paymentHistory = [];
      updatePaymentHistory(paymentHistory);
    }
  }, [paymentHistoryError]);

  useEffect(() => {
    initScriptTag({ env: process.env.REACT_APP_UNIFIED_PAYMENT_VERSION });
    document.addEventListener('UPC_setup', UPCSetup);
    setTooltipLabel(createTooltipLabel({ amount: priceData.balanceDue }));
    return () => {
      document.removeEventListener('UPC_setup', UPCSetup);
    };
  }, []);

  useEffect(() => {
    if (tooltipInfo && tooltipInfo.staterooms?.length) {
      setTooltipContent(
        <Box className={classes.tooltipWrapper}>
          <Box className={classes.tootipTilte}>
            {renderContent('bookingAmendPaymentFooterPaymentDetail', 'Payment detail')}
          </Box>
          <Box>
            {tooltipInfo.staterooms.map((stateroom) => {
              return stateroom.payments.map((payment, i) => {
                return (
                  <Box key={`${payment.paymentOption}-${i + 1}`} className={classes.tooltipRow}>
                    <span>
                      {`${mapingLabelToolTipInfo(payment.paymentOption)} ${payment.lastDigits}`}
                    </span>
                    <span>{payment.formattedAmount}</span>
                  </Box>
                );
              });
            })}
          </Box>
        </Box>,
      );
    }
  }, [tooltipInfo]);

  useEffect(() => {
    if (currentBookingInfo) {
      setLdFlagsParams({ ...ldFlagsParams, ldFlagsCountry, language: lang });
    }
  }, []);

  useEffect(() => {
    const manageVer = getCookies('manage_ver');
    const currentToken = currentBookingInfo?.token;

    if ((!manageVer || manageVer !== 'legacy') && currentToken) {
      setShowLoader(true);
      setIsRedirecting(true);
      const encoded = safeEncode(currentToken);
      if (encoded) {
        window.location.replace(`${REDIRECT_API_URL}${encoded}`);
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener('UPC_setup', UPCSetup);
    return () => {
      document.removeEventListener('UPC_setup', UPCSetup);
    };
  }, []);

  useEffect(() => {
    if (currentBookingData) {
      const {
        update: {
          remainingBalance: updateRemainingBalance,
          amountPaymentHistory: updateAmountPaymentHistory,
          paymentTypes: updatePaymentTypes,
        },
      } = window.UnifiedPaymentComponent;
      if (currentBookingData?.currentBooking) {
        const { id, price } = currentBookingData.currentBooking;
        const priceVar = {
          bookingId: id,
          balanceDue: price.balanceDue,
          balanceDueDate: price.balanceDueDate,
          currencyCode: price.currencyCode,
          inFinal: price.inFinal,
          total: price.total,
          totalBalanceDue: price.totalBalanceDue,
          paymentsApplied: price.paymentsApplied,
          depositAmountOwed: price.depositAmountOwed,
          depositAmount: price.depositAmount,
        };

        setCurrentPriceVar(priceVar);
        updateRemainingBalance(priceVar.balanceDue);
        updateAmountPaymentHistory(priceVar.total - priceVar.balanceDue);
        const paymentTypesUpdated = getPaymentTypes({ priceVar });
        updatePaymentTypes(paymentTypesUpdated);
      }
    }
  }, [currentBookingData]);

  useEffect(() => {
    if (Object.keys(paymentResult).length) {
      handlePayment();
    }
  }, [paymentResult]);

  if (isRedirecting) {
    return <Loader display />;
  }

  return (
    <>
      <Loader
        display={showLoader || currentBookingLoading}
        message={
          (showLoader || currentBookingLoading) &&
          !paymentHistoryLoading &&
          !isSubmittingPayment &&
          getDictionaryValue(Dictionary.loaderPaymentRedirect.key)
        }
      />
      <Header position="relative" elevation={0} className={classes.header}>
        <NavLink component="a" href={`${urlPath}/dashboard`}>
          <Typography variant="body2" className={classes.backLabel}>
            {renderContent('amendHeaderBackToCruise', 'Back to your cruise reservation')}
          </Typography>
        </NavLink>
      </Header>
      <Box className={classes.rootPayment}>
        <Box className={classes.contentWrapper}>
          <aside>
            {sailingData && (
              <Itinerary
                itinerary={sailingData}
                paymentOrigin
                className={classes.itinerary}
                bookingId={currentBookingInfo.id}
              />
            )}
          </aside>
          <main className={classes.mainUP}>
            <react-unified-payment
              brand={UNIFIED_PAYMENT_SERVICE_BRAND}
              client={UNIFIED_PAYMENT_SERVICE_CLIENT}
            />
          </main>
        </Box>
        {payRequired && (
          <PaymentFooterWithModal
            tooltipContent={tooltipContent}
            tooltipLabel={tooltipLabel}
            submitPayment={submitPayment}
            setFirstValueCheckbox={setFirstValueCheckbox}
            isCheckedCruiseTicket={isCheckedCruiseTicket}
            setIsCheckedCruiseTicket={setIsCheckedCruiseTicket}
            showLineError={showLineError}
            setShowLineError={setShowLineError}
            ctaTitle={ctaTitle}
          />
        )}
        <form
          ref={bankOfAmericaFormRef}
          method="post"
          action={bankOfAmericaSetup.url}
          name="boaForm"
          id="boaForm"
          target="_blank">
          <input
            type="hidden"
            name="requesthashvalue"
            id="requesthashvalue"
            value={bankOfAmericaSetup.requesthashvalue}
          />
          <input
            type="hidden"
            name="requestpayload"
            id="requestpayload"
            value={bankOfAmericaSetup.requestpayload}
          />
        </form>
      </Box>
    </>
  );
};
export default UnifiedPayment;
